import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Share2, ZoomIn, ZoomOut, RotateCw, Heart, MessageCircle } from 'lucide-react';
import { profileThumb, profileThumbFallback } from '../utils/imageHelpers';
import DownloadPaywall from '../components/DownloadPaywall';
import SignupInviteModal from '../components/SignupInviteModal';
import PasswordProtectedModal from '../components/PasswordProtectedModal';
import PaymentModal from '../components/PaymentModal';
import ContributingArtistsDisplay from '../components/ContributingArtistsDisplay';
import RichDescription from '../components/RichDescription';
import MetadataDisplay from '../components/MetadataDisplay';
import { useAuth } from '../context/AuthContext';
import { updateImageMeta } from '../utils/metaUtils';
import api from '../services/api';

const ImageViewer = () => {
    const { username, uploadId } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const [image, setImage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [downloadPaywall, setDownloadPaywall] = useState({ isOpen: false, image: null });
    const [comments, setComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'image' });
    const [passwordModal, setPasswordModal] = useState({ isOpen: false });
    const [paymentModal, setPaymentModal] = useState({ isOpen: false });
    const [hasAccess, setHasAccess] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const fetchComments = async (imageId) => {
        if (!imageId) return;
        try {
            setLoadingComments(true);
            const response = await api.get(`/upload/${imageId}/comments`);
            
            if (response.data.success) {
                setComments(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching comments:', err);
        } finally {
            setLoadingComments(false);
        }
    };

    useEffect(() => {
        const fetchImage = async () => {
            try {
                // Try to get specific image by username and filename
                let response;
                if (username && uploadId) {
                    // Try specific endpoint for image by username and filename
                    response = await api.get(`/upload/public/image/${username}/${uploadId}`);
                } else {
                    // Fallback to get by ID if we have numeric uploadId
                    response = await api.get(`/upload/${uploadId}`);
                }
                
                const result = response.data;
                
                if (result.success && result.data) {
                    const imageData = result.data;
                    setImage(imageData);
                    
                    // Update page meta tags for SEO and social sharing
                    updateImageMeta(imageData).catch(console.error);
                    
                    // Check if content requires special access
                    if (imageData.access_level === 'password_protected' || imageData.access_level === 'secret' || imageData.access_level === 'paid') {
                        // Check if user already has access
                        try {
                            const accessResponse = await api.get(`/upload/${imageData.id}/check-access`);
                            if (accessResponse.data.success && accessResponse.data.hasAccess) {
                                setHasAccess(true);
                            } else {
                                // Show appropriate modal based on access level
                                if (imageData.access_level === 'paid') {
                                    setPaymentModal({ isOpen: true });
                                } else {
                                    setPasswordModal({ isOpen: true });
                                }
                            }
                        } catch (accessErr) {
                            // If access check fails, show appropriate modal
                            if (imageData.access_level === 'paid') {
                                setPaymentModal({ isOpen: true });
                            } else {
                                setPasswordModal({ isOpen: true });
                            }
                        }
                    } else {
                        setHasAccess(true);
                    }
                } else {
                    setError('Image not found');
                }
            } catch (err) {
                console.error('Error fetching image:', err);
                // Fallback: try to get from public images list
                try {
                    const fallbackResponse = await api.get('/upload/public/image');
                    const fallbackResult = fallbackResponse.data;
                    
                    if (fallbackResult.success && fallbackResult.data.length > 0) {
                        // Try to find by filename or stored_filename
                        const imageData = fallbackResult.data.find(upload => 
                            (upload.stored_filename === uploadId || 
                             upload.original_filename === uploadId ||
                             upload.id == uploadId) &&
                            (!username || upload.username === username)
                        );
                        if (imageData) {
                            setImage(imageData);
                            
                            // Update page meta tags for SEO and social sharing
                            updateImageMeta(imageData).catch(console.error);
                        } else {
                            setError('Image not found');
                        }
                    } else {
                        setError('Image not found');
                    }
                } catch (fallbackErr) {
                    console.error('Fallback error:', fallbackErr);
                    setError('Failed to load image');
                }
            } finally {
                setLoading(false);
            }
        };


        if (uploadId) {
            fetchImage();
        }
    }, [uploadId, username]);

    // Separate effect to fetch comments when image is loaded
    useEffect(() => {
        if (image?.id) {
            fetchComments(image.id);
        }
    }, [image?.id]);

    // Handle escape key to exit expanded view
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && isExpanded) {
                setIsExpanded(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isExpanded]);

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: image.title,
                    text: image.description || 'Check out this image on The Deep Wave',
                    url: window.location.href
                });
            } catch (err) {
                console.log('Error sharing:', err);
            }
        } else {
            navigator.clipboard.writeText(window.location.href);
        }
    };

    const handleDownloadClick = () => {
        setDownloadPaywall({ isOpen: true, image });
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev * 1.5, 5));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev / 1.5, 0.5));
    };

    const handleRotate = () => {
        setRotation(prev => (prev + 90) % 360);
    };

    const resetView = () => {
        setZoom(1);
        setRotation(0);
    };

    const handleLike = async () => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'image' });
            return;
        }

        if (!image?.id) {
            console.error('No image ID available for like');
            return;
        }

        try {
            const response = await api.post(`/upload/${image.id}/like`);
            
            if (response.data.success) {
                setImage(prev => ({
                    ...prev,
                    like_count: response.data.data.likeCount,
                    user_has_liked: response.data.data.liked
                }));
            }
        } catch (err) {
            console.error('Error toggling like:', err);
        }
    };

    const handleCommentLike = async (commentId) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'image' });
            return;
        }

        try {
            const response = await api.post(`/upload/comments/${commentId}/like`);
            
            if (response.data.success) {
                setComments(prev => prev.map(comment => 
                    comment.id === commentId
                        ? { ...comment, like_count: response.data.data.likeCount, user_has_liked: response.data.data.liked }
                        : comment
                ));
            }
        } catch (err) {
            console.error('Error toggling comment like:', err);
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim() || !isAuthenticated || !image?.id) return;

        try {
            setSubmittingComment(true);
            const response = await api.post(`/upload/${image.id}/comments`, {
                content: newComment.trim()
            });
            
            if (response.data.success) {
                setComments(prev => [response.data.data, ...prev]);
                setNewComment('');
                setImage(prev => ({
                    ...prev,
                    comment_count: (prev.comment_count || 0) + 1
                }));
            }
        } catch (err) {
            console.error('Error adding comment:', err);
        } finally {
            setSubmittingComment(false);
        }
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
        if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)}w ago`;
        return date.toLocaleDateString();
    };

    const handlePasswordSubmit = async (password) => {
        if (!image?.id) return;
        
        try {
            const response = await api.post(`/upload/${image.id}/verify-password`, { password });
            
            if (response.data.success) {
                setHasAccess(true);
                setPasswordModal({ isOpen: false });
            } else {
                throw new Error(response.data.message || 'Invalid password');
            }
        } catch (err) {
            throw new Error(err.response?.data?.message || err.message || 'Access denied');
        }
    };

    const handlePaymentSubmit = async (paymentData) => {
        if (!image?.id) return;
        
        try {
            const response = await api.post(`/upload/${image.id}/purchase`, {
                paymentMethod: paymentData.paymentMethod,
                price: paymentData.price,
                currency: paymentData.currency
            });
            
            if (response.data.success) {
                setHasAccess(true);
                setPaymentModal({ isOpen: false });
            } else {
                throw new Error(response.data.message || 'Payment failed');
            }
        } catch (err) {
            throw new Error(err.response?.data?.message || err.message || 'Payment failed');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-white text-lg">Loading image...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-400 text-lg mb-4">{error}</div>
                    <button 
                        onClick={() => navigate('/')}
                        className="text-purple-400 hover:text-purple-300"
                    >
                        Go back to home
                    </button>
                </div>
            </div>
        );
    }

    // Don't render content if password protected and no access
    const shouldShowContent = hasAccess || (!image?.access_level || image.access_level === 'public');

    return (
        <div className="min-h-screen bg-black text-white">
            {/* Header */}
            <div className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-700 p-4 fixed top-0 left-0 right-0 z-10">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back
                    </button>

                    {/* Image Controls */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleZoomOut}
                            className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                            title="Zoom Out"
                        >
                            <ZoomOut className="w-4 h-4" />
                        </button>
                        
                        <span className="px-3 py-2 bg-gray-800 rounded-lg text-sm">
                            {Math.round(zoom * 100)}%
                        </span>
                        
                        <button
                            onClick={handleZoomIn}
                            className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                            title="Zoom In"
                        >
                            <ZoomIn className="w-4 h-4" />
                        </button>
                        
                        <button
                            onClick={handleRotate}
                            className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                            title="Rotate"
                        >
                            <RotateCw className="w-4 h-4" />
                        </button>
                        
                        <button
                            onClick={resetView}
                            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm"
                        >
                            Reset
                        </button>
                    </div>
                    
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleLike}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                                image?.user_has_liked
                                    ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' 
                                    : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                            }`}
                            title={isAuthenticated 
                                ? (image?.user_has_liked ? 'Unlike this artwork' : 'Like this artwork')
                                : 'Sign up to like artwork and support artists'
                            }
                        >
                            <Heart className={`w-4 h-4 ${image?.user_has_liked ? 'fill-current' : ''}`} />
                            {image?.like_count || 0}
                        </button>
                        <button
                            onClick={handleShare}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                            title="Share"
                        >
                            <Share2 className="w-4 h-4" />
                            Share
                        </button>
                        <button
                            onClick={handleDownloadClick}
                            className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                        >
                            <Download className="w-4 h-4" />
                            Download
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Image Area */}
            <div className={`${isExpanded ? 'fixed inset-0 z-50 bg-black' : 'pt-20 pb-8 min-h-screen'} flex items-center justify-center overflow-auto`}>
                {shouldShowContent ? (
                    <div className="relative group">
                        <img
                            src={image?.file_url}
                            alt={image?.title}
                            className="max-w-none transition-transform duration-300 ease-in-out shadow-2xl cursor-pointer"
                            style={{
                                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                                maxHeight: isExpanded ? '100vh' : '90vh',
                                maxWidth: isExpanded ? '100vw' : '90vw'
                            }}
                            onClick={() => setIsExpanded(!isExpanded)}
                            onDoubleClick={handleZoomIn}
                        />
                        
                        {/* Magnifying Glass Icon Overlay */}
                        {!isExpanded && (
                            <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                <ZoomIn className="w-6 h-6 text-white" />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <div className="w-32 h-32 bg-gray-800 rounded-lg flex items-center justify-center mb-4 mx-auto">
                                <div className="text-6xl">
                                    {image?.access_level === 'paid' ? '💎' : '🔒'}
                                </div>
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">
                                {image?.access_level === 'paid' ? 'Premium Content' : 'Protected Content'}
                            </h3>
                            <p className="text-gray-400">
                                {image?.access_level === 'paid' 
                                    ? `Purchase required: ${image?.price ? `$${image.price}` : 'Paid content'}`
                                    : 'This image is password protected'
                                }
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Image Info Panel */}
            <div className="fixed bottom-0 left-0 right-0 bg-gray-900/90 backdrop-blur-sm border-t border-gray-700 p-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Left - Title & Description */}
                        <div className="lg:col-span-1">
                            <h1 className="text-xl font-bold mb-2">{image?.title}</h1>
                            
                            {image?.description && (
                                <div className="mb-4">
                                    <h3 className="text-sm font-semibold text-gray-300 mb-1">Description</h3>
                                    <RichDescription
                                        description={image?.description}
                                        maxLength={150}
                                        showLabel={false}
                                        textClassName="text-gray-400 text-sm"
                                    />
                                </div>
                            )}
                            
                            {/* Contributing Artists */}
                            {image?.id && (
                                <ContributingArtistsDisplay
                                    mediaType="upload"
                                    mediaId={image.id}
                                    layout="horizontal"
                                    maxVisible={3}
                                    className="text-sm mb-4"
                                />
                            )}
                        </div>
                        
                        {/* Center - Creator & Engagement */}
                        <div className="lg:col-span-1">
                            <h3 className="text-sm font-semibold text-gray-300 mb-3">Artist</h3>
                            
                            {/* Creator Info */}
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                                    {image?.profile_picture_url ? (
                                        <img
                                            src={profileThumb(image.profile_picture_url)}
                                            alt={image.display_name}
                                            width="40"
                                            height="40"
                                            className="w-10 h-10 rounded-full object-cover"
                                            onError={profileThumbFallback(image.profile_picture_url)}
                                        />
                                    ) : (
                                        <span className="text-white font-semibold text-sm">
                                            {image?.display_name?.charAt(0) || image?.username?.charAt(0) || 'U'}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <p className="text-sm font-medium">{image?.display_name || image?.username}</p>
                                    <p className="text-gray-400 text-xs">@{image?.username}</p>
                                </div>
                            </div>

                            {/* Engagement Stats */}
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={handleLike}
                                    className={`flex items-center gap-2 px-3 py-1 rounded-lg transition-colors text-sm ${
                                        image?.user_has_liked
                                            ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' 
                                            : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                                    }`}
                                >
                                    <Heart className={`w-4 h-4 ${image?.user_has_liked ? 'fill-current' : ''}`} />
                                    <span>{image?.like_count || 0}</span>
                                </button>

                                <div className="flex items-center gap-2 text-gray-400 text-sm">
                                    <MessageCircle className="w-4 h-4" />
                                    <span>{image?.comment_count || 0}</span>
                                </div>
                            </div>
                        </div>

                        {/* Right - Details Panel */}
                        <div className="lg:col-span-1">
                            <h3 className="text-sm font-semibold text-gray-300 mb-3">Details</h3>
                            
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div>
                                    <span className="text-gray-400">Type</span>
                                    <div className="text-white">{image?.file_type?.toUpperCase() || '-'}</div>
                                </div>
                                
                                <div>
                                    <span className="text-gray-400">Size</span>
                                    <div className="text-white">
                                        {image?.file_size 
                                            ? `${(image.file_size / (1024 * 1024)).toFixed(1)} MB`
                                            : '-'
                                        }
                                    </div>
                                </div>
                                
                                <div>
                                    <span className="text-gray-400">Format</span>
                                    <div className="text-white">
                                        {image?.original_filename?.split('.').pop()?.toUpperCase() || '-'}
                                    </div>
                                </div>
                                
                                <div>
                                    <span className="text-gray-400">Resolution</span>
                                    <div className="text-white">
                                        {image?.width && image?.height 
                                            ? `${image.width}x${image.height}`
                                            : '-'
                                        }
                                    </div>
                                </div>
                                
                                <div>
                                    <span className="text-gray-400">Artist</span>
                                    <div className="text-white">{image?.artist || '-'}</div>
                                </div>
                                
                                <div>
                                    <span className="text-gray-400">Medium</span>
                                    <div className="text-white">{image?.medium || '-'}</div>
                                </div>
                                
                                <div>
                                    <span className="text-gray-400">Dimensions</span>
                                    <div className="text-white">{image?.dimensions || '-'}</div>
                                </div>
                                
                                <div>
                                    <span className="text-gray-400">Year</span>
                                    <div className="text-white">{image?.year || '-'}</div>
                                </div>
                                
                                <div>
                                    <span className="text-gray-400">Category</span>
                                    <div className="text-white">{image?.category || '-'}</div>
                                </div>
                                
                                <div>
                                    <span className="text-gray-400">Tags</span>
                                    <div className="text-white">
                                        {image?.tags ? (
                                            Array.isArray(image.tags) 
                                                ? image.tags.slice(0, 2).join(', ') + (image.tags.length > 2 ? '...' : '')
                                                : image.tags.slice(0, 30) + (image.tags.length > 30 ? '...' : '')
                                        ) : '-'}
                                    </div>
                                </div>
                                
                                <div>
                                    <span className="text-gray-400">Views</span>
                                    <div className="text-white">{image?.view_count || 0}</div>
                                </div>
                                
                                <div>
                                    <span className="text-gray-400">Visibility</span>
                                    <div className="text-white">
                                        {image?.is_public ? 'Public' : 'Private'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Comments Section */}
                <div className="mt-8">
                    <h3 className="text-xl font-bold text-white mb-6">
                        Comments ({image?.comment_count || 0})
                    </h3>

                    {/* Add Comment Form */}
                    {isAuthenticated ? (
                        <div className="bg-gray-900/80 backdrop-blur-sm rounded-lg p-6 mb-6">
                            <form onSubmit={handleAddComment}>
                                <textarea
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Add a comment..."
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg p-4 text-white placeholder-gray-400 resize-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                                    rows="3"
                                />
                                <div className="flex justify-end mt-4">
                                    <button
                                        type="submit"
                                        disabled={!newComment.trim() || submittingComment}
                                        className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                                    >
                                        {submittingComment ? 'Posting...' : 'Post Comment'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <div className="bg-gray-900/80 backdrop-blur-sm rounded-lg p-6 mb-6 text-center">
                            <div className="text-4xl mb-4">💬</div>
                            <h3 className="text-xl font-bold text-white mb-2">Join the conversation</h3>
                            <p className="text-gray-400 mb-6">
                                Login to share your thoughts and connect with other members of The Deep Wave community.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <button
                                    onClick={() => navigate('/login')}
                                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                                >
                                    Login to Comment
                                </button>
                                <button
                                    onClick={() => navigate('/register')}
                                    className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                                >
                                    Create Account
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Comments List */}
                    <div className="space-y-4">
                        {loadingComments ? (
                            <div className="text-center py-8">
                                <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                                <p className="text-gray-400">Loading comments...</p>
                            </div>
                        ) : comments.length > 0 ? (
                            comments.map((comment) => (
                                <div key={comment.id} className="bg-gray-900/70 backdrop-blur-sm rounded-lg p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
                                            {comment.avatar ? (
                                                <img
                                                    src={profileThumb(comment.avatar)}
                                                    alt={comment.display_name}
                                                    width="40"
                                                    height="40"
                                                    className="w-10 h-10 rounded-full object-cover"
                                                    onError={profileThumbFallback(comment.avatar)}
                                                />
                                            ) : (
                                                <span className="text-white font-semibold text-sm">
                                                    {(comment.display_name || comment.username)?.charAt(0) || 'U'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h4 className="font-semibold text-white">
                                                    {comment.display_name || comment.username}
                                                </h4>
                                                <span className="text-sm text-gray-500">
                                                    {formatTimeAgo(comment.created_at)}
                                                </span>
                                            </div>
                                            <p className="text-gray-300 leading-relaxed mb-3">
                                                {comment.content}
                                            </p>
                                            <button
                                                onClick={() => handleCommentLike(comment.id)}
                                                className={`flex items-center gap-2 px-3 py-1 rounded-lg transition-colors text-sm ${
                                                    comment.user_has_liked
                                                        ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' 
                                                        : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                                                }`}
                                            >
                                                <Heart className={`w-4 h-4 ${comment.user_has_liked ? 'fill-current' : ''}`} />
                                                <span>{comment.like_count || 0}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8">
                                <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                                <p className="text-gray-400">No comments yet. Be the first to comment!</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Download Paywall Modal */}
            <DownloadPaywall
                isOpen={downloadPaywall.isOpen}
                onClose={() => setDownloadPaywall({ isOpen: false, image: null })}
                mediaTitle={downloadPaywall.image?.title || 'Unknown Image'}
                mediaType="image"
                uploadId={downloadPaywall.image?.id}
            />

            {/* Signup Invite Modal */}
            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'image' })}
                contentType={signupInvite.contentType}
            />

            {/* Password Protection Modal */}
            <PasswordProtectedModal
                isOpen={passwordModal.isOpen}
                onClose={() => navigate(-1)}
                onSubmit={handlePasswordSubmit}
                contentTitle={image?.title || image?.original_filename}
                contentType="image"
            />

            {/* Payment Modal */}
            <PaymentModal
                isOpen={paymentModal.isOpen}
                onClose={() => navigate(-1)}
                onSubmit={handlePaymentSubmit}
                contentTitle={image?.title || image?.original_filename}
                contentType="image"
                price={image?.price}
                currency={image?.currency}
                creatorName={image?.display_name || image?.username}
            />
        </div>
    );
};

export default ImageViewer;